import { cn } from "@/lib/utils";
import type { Domain } from "@/types/domain";
import { EditableCell, type EditableCellValue } from "./EditableCell";
import { CopyIconButton } from "@/components/clicks/CopyableValue";
import {
  STATUS_OPTIONS,
  SITE_TYPE_OPTIONS,
  DOMAIN_TYPE_OPTIONS,
  CMS_OPTIONS,
  BRAND_TYPE_OPTIONS,
  OFFER_OPTIONS,
} from "@/constants/domain-options";
import {
  getStatusStyles,
  getSiteTypeStyles,
  getDomainTypeStyles,
  getCmsStyles,
  getBrandTypeStyles,
  getOfferStyles,
} from "@/lib/table-styles";

interface DomainCellProps {
  domain: Domain;
  columnKey: string;
  onUpdate: (id: string, column: string, value: EditableCellValue) => Promise<void>;
}

const BOOLEAN_COLUMNS = new Set([
  "bought",
  "ns",
  "ai_bot_protection_disabled",
  "translated",
  "live",
  "index",
  "add_page_blocks",
  "add_page_content",
  "add_to_domains",
  "add_to_dynahosting",
  "bing_connected",
  "google_analytics",
  "gsc_connected",
  "is_hreflang_group",
  "seogets",
  "taxonomy_data",
  "wordpress_setup",
]);

const JETSIK_COLUMNS = new Set(["geo", "lang", "brand_keyword"]);

export function DomainCell({ domain, columnKey, onUpdate }: DomainCellProps) {
  const raw = domain[columnKey as keyof Domain];
  const value = raw ?? null;

  // DROPDOWN FIELDS
  if (columnKey === "status") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="status"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={STATUS_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[180px] justify-center",
          getStatusStyles(String(value)),
        )}
      />
    );
  }

  if (columnKey === "site_type") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="site_type"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={SITE_TYPE_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[140px] justify-center",
          getSiteTypeStyles(String(value)),
        )}
      />
    );
  }

  if (columnKey === "domain_type") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="domain_type"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={DOMAIN_TYPE_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[100px] justify-center",
          getDomainTypeStyles(String(value)),
        )}
      />
    );
  }

  if (columnKey === "cms") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="cms"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={CMS_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[120px] justify-center",
          getCmsStyles(String(value)),
        )}
      />
    );
  }

  if (columnKey === "brand_type") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="brand_type"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={BRAND_TYPE_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[100px] justify-center",
          getBrandTypeStyles(String(value)),
        )}
      />
    );
  }

  // Filled from Jetsik (see DashboardService), so there is nothing to edit
  // here: a change belongs in Jetsik and arrives with the next sync.
  if (JETSIK_COLUMNS.has(columnKey)) {
    return (
      <div
        className={cn(
          "px-2 py-1 min-h-[28px] flex items-center truncate max-w-[200px]",
          !value && "text-muted-foreground/70 italic",
        )}
        title={value ? String(value) : undefined}
      >
        {value ? String(value) : "Empty"}
      </div>
    );
  }

  if (columnKey === "offer") {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column="offer"
        value={value}
        onUpdate={onUpdate}
        type="select"
        options={OFFER_OPTIONS}
        className={cn(
          "px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap w-[100px] justify-center",
          getOfferStyles(String(value)),
        )}
      />
    );
  }

  // CHECKBOX FIELDS
  if (typeof value === "boolean" || BOOLEAN_COLUMNS.has(columnKey)) {
    return (
      <EditableCell
        rowId={String(domain.id)}
        column={columnKey}
        value={!!value}
        onUpdate={onUpdate}
        type="checkbox"
      />
    );
  }

  // DOMAIN: the row's identity — a copy affordance beside the (editable) value,
  // shown in full (no max-width cap, so it is not truncated).
  if (columnKey === "domain") {
    return (
      <div className="flex items-center gap-1">
        <EditableCell
          rowId={String(domain.id)}
          column={columnKey}
          value={value}
          onUpdate={onUpdate}
          className={cn(!value && "text-muted-foreground/70 italic")}
        />
        <CopyIconButton value={value == null ? undefined : String(value)} />
      </div>
    );
  }

  // TEXT FIELDS (Default)
  return (
    <EditableCell
      rowId={String(domain.id)}
      column={columnKey}
      value={value}
      onUpdate={onUpdate}
      className={cn("max-w-[200px]", !value && "text-muted-foreground/70 italic")}
    />
  );
}
